<script setup>
import { computed, ref } from "vue";
import { RouterLink, RouterView, useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { useAuthStore } from "@/stores/auth";

const auth = useAuthStore();
const route = useRoute();
const { mdAndUp } = useDisplay();

const drawer = ref(true);

const navGroups = [
  {
    heading: "Operations",
    items: [
      { title: "Dashboard", name: "Home", icon: "mdi-view-dashboard-outline" },
      { title: "Bookings", name: "Bookings", icon: "mdi-calendar-check-outline" },
      { title: "Trail Information", name: "Trails", icon: "mdi-map-marker-path" },
      { title: "Weather", name: "Weather", icon: "mdi-weather-partly-cloudy" },
      { title: "Car Park", name: "CarPark", icon: "mdi-car-multiple" },
      { title: "Documents", name: "Documents", icon: "mdi-folder-file-outline" },
      { title: "Safety", name: "SafetyPage", icon: "mdi-shield-check-outline" },
      { title: "Enjoy the Outdoors", name: "ErosionPage", icon: "mdi-pine-tree" },
      { title: "Notifications", name: "NotificationsPage", icon: "mdi-bell-outline" },
    ],
  },
  {
    heading: "Utilities",
    items: [
      { title: "Emails", name: "EmailsPage", icon: "mdi-email-edit-outline" },
      { title: "Users", name: "Users", icon: "mdi-account-group-outline" },
      { title: "Logs", name: "Logs", icon: "mdi-text-box-search-outline" },
      { title: "Footfall", name: "Footfall", icon: "mdi-traffic-light" },
      { title: "Disclaimer", name: "DisclaimerPage", icon: "mdi-file-document-alert-outline" },
    ],
  },
];

const currentTitle = computed(() => {
  const item = navGroups
    .flatMap((group) => group.items)
    .find((entry) => entry.name === route.name);
  return item?.title || "Errigal Admin";
});
</script>

<template>
  <v-app>
    <v-navigation-drawer
      v-model="drawer"
      :permanent="mdAndUp"
      :temporary="!mdAndUp"
      color="primary"
      width="252"
    >
      <div class="sidebar-shell">
        <div class="sidebar-brand">
          <RouterLink to="/" class="brand-link">
            <span class="brand-mark">
              <img src="/logo.png" alt="Errigal logo" class="brand-logo" />
            </span>
            <div>
              <div class="brand-title">Errigal Admin</div>
              <div class="brand-subtitle">Operations</div>
            </div>
          </RouterLink>
        </div>

        <v-list nav density="comfortable" class="sidebar-nav">
          <template v-for="group in navGroups" :key="group.heading">
            <v-list-subheader class="sidebar-subheader">{{ group.heading }}</v-list-subheader>
            <v-list-item
              v-for="item in group.items"
              :key="item.name"
              :to="{ name: item.name }"
              :prepend-icon="item.icon"
              :title="item.title"
              rounded="lg"
              color="white"
            />
          </template>
        </v-list>

        <div class="sidebar-footer">
          <div class="sidebar-user">
            <div class="sidebar-user-label">Signed in</div>
            <div class="sidebar-user-value">{{ auth.user?.profile?.name || auth.user?.profile?.email || "Admin user" }}</div>
          </div>
          <v-btn
            v-if="auth.isAuthenticated"
            block
            variant="tonal"
            color="white"
            prepend-icon="mdi-logout"
            @click="auth.logout()"
          >
            Sign out
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <v-app-bar flat color="surface">
      <v-btn
        v-if="!mdAndUp"
        icon="mdi-menu"
        variant="text"
        @click="drawer = !drawer"
      />
      <v-app-bar-title class="text-body-1 font-weight-semibold">
        {{ currentTitle }}
      </v-app-bar-title>
    </v-app-bar>

    <v-main class="admin-main">
      <RouterView />
    </v-main>
  </v-app>
</template>

<style scoped>
.sidebar-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 16px 12px 12px;
  color: white;
  background:
    linear-gradient(180deg, rgba(20, 63, 50, 0.9) 0%, rgba(15, 46, 37, 0.95) 100%),
    url("/errigal.jpg") center center / cover no-repeat;
}

.sidebar-shell::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0) 22%);
  pointer-events: none;
}

.sidebar-shell > * {
  position: relative;
  z-index: 1;
}

.sidebar-brand {
  padding: 4px 8px 16px;
}

.brand-link {
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  text-decoration: none;
}

.brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.14);
  overflow: hidden;
  box-shadow: 0 10px 24px -18px rgba(0, 0, 0, 0.55);
}

.brand-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: rgba(255, 255, 255, 0.96);
  padding: 6px;
}

.brand-title {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.1;
}

.brand-subtitle {
  font-size: 0.78rem;
  opacity: 0.75;
}

.sidebar-nav {
  flex: 1;
}

.sidebar-subheader {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding-inline: 12px;
  min-height: 32px;
}

.sidebar-footer {
  padding: 16px 8px 4px;
  display: grid;
  gap: 12px;
}

.sidebar-user {
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
}

.sidebar-user-label {
  font-size: 0.72rem;
  opacity: 0.72;
  margin-bottom: 2px;
}

.sidebar-user-value {
  font-size: 0.88rem;
  font-weight: 500;
  word-break: break-word;
}

.admin-main {
  background: #f6f7f9;
}
</style>
